import { useEffect, useRef, useState } from 'react';
import type { Review } from './types';
import { mediaPerReview } from './format';
import type { MediaItem } from './format';
import { captionText } from './format';
import { Ticker } from './Ticker';
import { StoreImage } from '../../ui/StoreImage';

/**
 * En video i raden: hämtas först när den syns. Originalet spelar och pausas
 * när det rullat ut; kopiorna som fyller ut loopen (play=false) visar bara
 * första bilden, så att videon inte laddas ner en gång per kopia.
 */
function TickerVideo({ src, poster, play }: { src: string; poster?: string; play: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!play) return;
    if (visible) {
      el.muted = true; // krävs för att få spela utan ljud på mobilen
      el.play().catch(() => { /* webbläsaren tillät inte uppspelning: förhandsbilden står kvar */ });
    } else {
      el.pause();
    }
  }, [visible, play]);

  const preload = !visible ? 'none' : play ? 'auto' : 'metadata';
  return <video ref={ref} src={src} poster={poster} muted loop playsInline preload={preload} aria-hidden={play ? undefined : true} />;
}

/** "Från våra kunder": bilder och videor från recensionerna. En per recension, inga dubletter. Namnet under. */
export function MediaTicker({ reviews, extra = [], title = 'Från våra kunder' }: { reviews: Review[]; extra?: MediaItem[]; title?: string }) {
  // Kundbilder utan recension (admin) kommer efter, och samma fil visas aldrig två gånger.
  const fromReviews = mediaPerReview(reviews);
  const seen = new Set(fromReviews.map((m) => m.media.url.trim()));
  const items = [...fromReviews, ...extra.filter((m) => !seen.has(m.media.url.trim()) && !!seen.add(m.media.url.trim()))];
  if (items.length === 0) return null;
  return (
    <section className="cn-section">
      <Ticker
        label={title}
        title={title}
        items={items}
        getKey={(m) => m.key}
        secondsPerItem={5}
        renderItem={(m, copy) => {
          const name = captionText(m.name);
          const poster = m.media.posterUrl ?? undefined;
          return (
          <figure className="cn-tick-media">
            <div className="cn-tick-media__frame">
              {m.media.type !== 'video' ? (
                <StoreImage src={m.media.url} width={640} alt={name ? `Bild från ${name}` : ''} loading="lazy" decoding="async" />
              ) : copy > 0 && poster ? (
                // Kopiorna som fyller ut loopen visar bara förhandsbilden.
                <StoreImage src={poster} width={640} alt="" loading="lazy" decoding="async" />
              ) : (
                <TickerVideo src={m.media.url} poster={poster} play={copy === 0} />
              )}
            </div>
            {name && <figcaption>{name}</figcaption>}
          </figure>
          );
        }}
      />
    </section>
  );
}
